import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  useUserSettings,
  useSubscriptionDetails,
  useBillingHistory,
  useUpdateSettings,
  useChangePassword,
  useCancelSubscription,
  useResumeSubscription,
  useDeleteAccount,
  useCreatePortalSession,
} from '../hooks/useSettings';

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  // Business info removed - now managed at agent level
  notifications: {
    email: boolean;
    sms: boolean;
    frequency: string;
  };
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}


const Settings: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('account');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // React Query hooks
  const { data: userSettingsData, isLoading } = useUserSettings();
  const shouldLoadBilling = activeTab === 'billing' && (user?.hasActiveSubscription || user?.isInGracePeriod || !!user?.stripeCustomerId);
  const { data: subscriptionData, isLoading: billingLoading } = useSubscriptionDetails(shouldLoadBilling);
  const { data: billingHistoryData } = useBillingHistory(shouldLoadBilling);
  
  // Mutations
  const updateSettingsMutation = useUpdateSettings();
  const changePasswordMutation = useChangePassword();
  const cancelSubscriptionMutation = useCancelSubscription();
  const resumeSubscriptionMutation = useResumeSubscription();
  const deleteAccountMutation = useDeleteAccount();
  const createPortalSessionMutation = useCreatePortalSession();
  
  // Initialize profile with simplified user data
  const [profile, setProfile] = useState<UserProfile>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    // Business info removed - now managed at agent level
    notifications: {
      email: true,
      sms: false,
      frequency: 'immediate'
    }
  });

  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update profile when data arrives from React Query
  useEffect(() => {
    if (userSettingsData?.settings) {
      setProfile(prevProfile => ({
        ...prevProfile,
        // Only use notifications from settings, business info removed
        firstName: userSettingsData.settings.firstName || user?.firstName || prevProfile.firstName,
        lastName: userSettingsData.settings.lastName || user?.lastName || prevProfile.lastName,
        notifications: userSettingsData.settings.notifications || prevProfile.notifications,
        email: user?.email || prevProfile.email
      }));
    }
  }, [userSettingsData, user?.firstName, user?.lastName, user?.email]);

  // Extract data from React Query responses
  const subscriptionDetails = subscriptionData?.subscription || null;
  const billingHistory = billingHistoryData?.billingHistory || [];
  const isSaving = updateSettingsMutation.isPending;

  // Mutation handlers

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelSubscription = async () => {
    cancelSubscriptionMutation.mutate(true, {
      onSuccess: () => {
        showSuccess('Subscription will be canceled at the end of your billing period');
        setShowCancelConfirm(false);
        // Refresh user data
        refreshUser().catch(error => console.error('Failed to refresh user:', error));
      },
      onError: (error) => {
        showError(error.message || 'Failed to cancel subscription');
      }
    });
  };

  const scrollToPricing = () => {
    // Navigate to home page with pricing hash
    window.location.href = '/#pricing';
  };

  const resumeSubscription = async () => {
    resumeSubscriptionMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess('Subscription resumed successfully');
        // Refresh user data
        refreshUser().catch(error => console.error('Failed to refresh user:', error));
      },
      onError: (error) => {
        showError(error.message || 'Failed to resume subscription');
      }
    });
  };

  const openCustomerPortal = async () => {
    createPortalSessionMutation.mutate(undefined, {
      onSuccess: (data) => {
        window.open(data.url, '_blank');
      },
      onError: (error) => {
        showError(error.message || 'Failed to open customer portal');
      }
    });
  };

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle URL query parameters for tab selection

  // Handle URL query parameters for tab selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab && ['account', 'billing', 'notifications', 'security'].includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  const saveSettings = async () => {
    updateSettingsMutation.mutate(profile, {
      onSuccess: () => {
        showSuccess('Settings updated successfully');
        // Refresh user data to update the UI with new name
        refreshUser().catch(error => console.error('Failed to refresh user:', error));
      },
      onError: (error) => {
        showError(error.message || 'Failed to update settings');
      }
    });
  };

  const changePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    }, {
      onSuccess: () => {
        showSuccess('Password changed successfully');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      },
      onError: (error) => {
        showError(error.message || 'Failed to change password');
      }
    });
  };

  const deleteAccount = async () => {
    deleteAccountMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess('Account deleted successfully');
        logout();
      },
      onError: (error) => {
        showError(error.message || 'Failed to delete account');
      }
    });
  };

  const tabs = [
    { id: 'account', name: 'Account', icon: 'user' },
    { id: 'profile', name: 'Profile', icon: 'building' },
    { id: 'billing', name: 'Billing', icon: 'credit-card' },
    { id: 'notifications', name: 'Notifications', icon: 'bell' },
    { id: 'security', name: 'Security', icon: 'shield' }
  ];

  const getTabIcon = (icon: string) => {
    const iconClass = "w-5 h-5";
    switch (icon) {
      case 'user':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
      case 'building':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
      case 'credit-card':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>;
      case 'bell':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 2a6 6 0 00-6 6c0 1.887-.454 3.665-1.257 5.234a.75.75 0 00.515 1.076 32.91 32.91 0 003.256.508 3.5 3.5 0 006.972 0 32.91 32.91 0 003.256-.508.75.75 0 00.515-1.076A11.448 11.448 0 0116 8a6 6 0 00-6-6zM8.05 14.943a33.54 33.54 0 003.9.574 2 2 0 01-.309.806 2 2 0 01-3.182 0 2 2 0 01-.31-.806 33.54 33.54 0 00-.1-.574z" /></svg>;
      case 'shield':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {getTabIcon(tab.icon)}
                  <span className="ml-3">{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700">
              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Account Information</h2>
                    <p className="text-gray-600 dark:text-gray-300">Update your basic account details</p>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profile.firstName}
                          onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profile.lastName}
                          onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="max-w-md">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={profile.email}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={saveSettings}
                        disabled={isSaving}
                        className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Tab - Business info moved to agent level */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Business Profile</h2>
                    <p className="text-gray-600 dark:text-gray-300">Business information is now managed at the agent level</p>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Business Settings Moved
                        </h3>
                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                          Business information like name, hours, address, and phone are now configured when creating or editing agents. 
                          This allows you to have different business information for each agent if needed.
                        </p>
                        <div className="mt-4">
                          <a
                            href="/dashboard"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            Go to Dashboard
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Notification Preferences</h2>
                    <p className="text-gray-600 dark:text-gray-300">Choose how you want to be notified</p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Notifications</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Receive updates via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={profile.notifications.email}
                            onChange={(e) => setProfile({
                              ...profile,
                              notifications: { ...profile.notifications, email: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between opacity-60">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">SMS Notifications</h3>
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                              Coming Soon
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Receive updates via text message</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-not-allowed">
                          <input
                            type="checkbox"
                            checked={profile.notifications.sms}
                            onChange={(e) => setProfile({
                              ...profile,
                              notifications: { ...profile.notifications, sms: e.target.checked }
                            })}
                            disabled
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notification Frequency
                      </label>
                      <select
                        value={profile.notifications.frequency}
                        onChange={(e) => setProfile({
                          ...profile,
                          notifications: { ...profile.notifications, frequency: e.target.value }
                        })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="immediate">Immediate</option>
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={saveSettings}
                        disabled={isSaving}
                        className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Security Settings</h2>
                    <p className="text-gray-600 dark:text-gray-300">Manage your password and account security</p>
                  </div>

                  <div className="space-y-8">
                    {/* Password Change */}
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-8">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Change Password</h3>
                      <div className="space-y-4 max-w-md">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={changePassword}
                          className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-200"
                        >
                          Change Password
                        </button>
                      </div>
                    </div>

                    {/* Account Deletion */}
                    <div>
                      <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-4">Danger Zone</h3>
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <h4 className="text-sm font-medium text-red-800 dark:text-red-300">Delete Account</h4>
                            <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                              This action cannot be undone. This will permanently delete your account and all your agents.
                            </p>
                            <div className="mt-4">
                              {!showDeleteConfirm ? (
                                <button
                                  onClick={() => setShowDeleteConfirm(true)}
                                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                                >
                                  Delete Account
                                </button>
                              ) : (
                                <div className="space-y-3">
                                  <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                                    Are you absolutely sure? This action cannot be undone.
                                  </p>
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={deleteAccount}
                                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                                    >
                                      Yes, Delete Account
                                    </button>
                                    <button
                                      onClick={() => setShowDeleteConfirm(false)}
                                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm font-medium rounded-lg transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Billing & Subscription</h2>
                    <p className="text-gray-600 dark:text-gray-300">Manage your subscription and billing information</p>
                  </div>

                  {billingLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="lg" text="Loading billing information..." />
                    </div>
                  ) : !user?.hasActiveSubscription && !user?.isInGracePeriod && !user?.stripeCustomerId ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Active Subscription</h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-6">Subscribe to Svarra Complete to unlock all features</p>
                      <button
                        onClick={scrollToPricing}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-105"
                      >
                        View Pricing Plans
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {/* Current Subscription - only show if user has active or grace period subscription */}
                      {(user?.hasActiveSubscription || user?.isInGracePeriod) && (
                        <>
                          <div className="border-b border-gray-200 dark:border-gray-700 pb-8">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Subscription</h3>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Svarra Complete</h4>
                                  <p className="text-gray-600 dark:text-gray-300">
                                    {user?.displayStatus || subscriptionDetails?.status}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {subscriptionDetails ? formatCurrency(subscriptionDetails.amount, subscriptionDetails.currency) : '$250.00'}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-300">per month</p>
                                </div>
                              </div>
                              
                              {subscriptionDetails && (
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Billing Period:</span>
                                    <span className="text-gray-900 dark:text-white">
                                      {formatDate(subscriptionDetails.currentPeriodStart)} - {formatDate(subscriptionDetails.currentPeriodEnd)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Next Billing Date:</span>
                                    <span className="text-gray-900 dark:text-white">
                                      {formatDate(subscriptionDetails.currentPeriodEnd)}
                                    </span>
                                  </div>
                                  {subscriptionDetails.paymentMethod?.card && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-300">Payment Method:</span>
                                      <span className="text-gray-900 dark:text-white">
                                        {subscriptionDetails.paymentMethod.card.brand.toUpperCase()} •••• {subscriptionDetails.paymentMethod.card.last4}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Subscription Actions */}
                          <div className="border-b border-gray-200 dark:border-gray-700 pb-8">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Subscription Actions</h3>
                            <div className="space-y-4">
                              {subscriptionDetails?.cancelAtPeriodEnd ? (
                                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                      <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <div className="ml-3 flex-1">
                                      <h4 className="text-sm font-medium text-orange-800 dark:text-orange-300">
                                        Subscription Scheduled for Cancellation
                                      </h4>
                                      <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                                        Your subscription will be canceled on {subscriptionDetails && formatDate(subscriptionDetails.currentPeriodEnd)}. 
                                        You'll continue to have access until then.
                                      </p>
                                      <div className="mt-4">
                                        <button
                                          onClick={resumeSubscription}
                                          disabled={resumeSubscriptionMutation.isPending}
                                          className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                                        >
                                          {resumeSubscriptionMutation.isPending ? 'Processing...' : 'Resume Subscription'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-4">
                                  <button
                                    onClick={handleCancelClick}
                                    disabled={cancelSubscriptionMutation.isPending}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                                  >
                                    {cancelSubscriptionMutation.isPending ? 'Processing...' : 'Cancel Subscription'}
                                  </button>
                                  <button
                                    onClick={openCustomerPortal}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                                  >
                                    Update Payment Method
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Show subscribe option for users without active subscription */}
                      {!user?.hasActiveSubscription && !user?.isInGracePeriod && (
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-8">
                          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                              {user?.stripeCustomerId ? 'Subscription Expired' : 'No Active Subscription'}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                              {user?.stripeCustomerId ? 'Resubscribe to continue creating AI agents' : 'Subscribe now to start creating AI agents and unlock all features'}
                            </p>
                            {/* BETA MODE: Subscription disabled
                            <button
                              onClick={scrollToPricing}
                              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-105"
                            >
                              {user?.stripeCustomerId ? 'Resubscribe Now' : 'Subscribe Now'}
                            </button> */}
                            <button
                              disabled={true}
                              className="inline-flex items-center px-6 py-3 bg-gray-400 dark:bg-gray-600 text-white font-medium rounded-lg cursor-not-allowed opacity-60"
                            >
                              <span className="flex items-center">
                                Coming Soon
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                              </span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Billing History */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing History</h3>
                        {billingHistory.length === 0 ? (
                          <p className="text-gray-600 dark:text-gray-300">No billing history available.</p>
                        ) : (
                          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                              <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Date
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Amount
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Invoice
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                                {billingHistory.map((item) => (
                                  <tr key={item.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                      {formatDate(item.date)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                      {item.description}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                      {formatCurrency(item.amount, item.currency)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        item.status === 'paid' 
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      }`}>
                                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                      {item.pdfUrl && (
                                        <a
                                          href={item.pdfUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                        >
                                          Download PDF
                                        </a>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={confirmCancelSubscription}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period."
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
        type="warning"
        isLoading={cancelSubscriptionMutation.isPending}
      />
    </div>
  );
};

export default Settings;